/**
 * What a contextual card's button does. The card lives outside any canvas, so the canvas
 * commands (`set_occasion`, `pin`, `regenerate`) have nothing to act on here and are
 * ignored; navigation and the playground door run. `useAiraCommandNavigator` is the part
 * the chat drawer shares.
 */
import { useRouter } from 'vue-router'
import type { AiraCommand, AiraContextualButton } from '@/features/aira/api'
import { useAiraContextualStore } from './useAiraContextualStore'
import {
  buildFairStyleSpacePlaygroundLocation,
  buildFairStyleSpaceV2CapsuleLocation,
  buildFairStyleSpaceV2OutfitLocation,
} from '@/widgets/style-space/fair-v2/fair-style-space-v2-routes.data'
import { guessFairStyleSpaceCategory } from '@/widgets/style-space/fair/fair-style-space-outfit-guests.data'
import { STYLE_SPACE_ROUTE } from '@/router/routes'

/** Navigation commands, outside any canvas. Returns whether the command was one of them. */
export function useAiraCommandNavigator() {
  const router = useRouter()

  function navigate(command: AiraCommand): boolean {
    switch (command.type) {
      case 'open_product':
        if (command.variantId == null) return false
        void router.push({
          name: 'product',
          params: { productId: String(command.productId), productVariantId: String(command.variantId) },
        })
        return true
      case 'navigate':
        if (command.to === 'product') {
          if (command.variantId == null) return false
          void router.push({
            name: 'product',
            params: { productId: String(command.id), productVariantId: String(command.variantId) },
          })
        } else if (command.to === 'capsule') {
          void router.push(buildFairStyleSpaceV2CapsuleLocation(command.id))
        } else {
          void router.push(buildFairStyleSpaceV2OutfitLocation(command.id))
        }
        return true
      case 'style_product':
        // the canvas needs a picture to draw the piece; without one it opens empty
        if (!command.image) {
          void router.push({ path: STYLE_SPACE_ROUTE })
          return true
        }
        void router.push(
          buildFairStyleSpacePlaygroundLocation({
            guest: {
              productId: command.productId,
              variantId: command.variantId ?? null,
              imageSrc: command.image,
              category: command.category ?? guessFairStyleSpaceCategory(command.name),
              name: command.name ?? '',
              brand: command.brand ?? null,
            },
            itemSource: command.itemSource ?? undefined,
          }),
        )
        return true
      default:
        // canvas-only commands have no canvas here
        return false
    }
  }

  return { navigate }
}

export function useAiraContextualCommands() {
  const store = useAiraContextualStore()
  const { navigate } = useAiraCommandNavigator()

  function run(tipId: string, button: AiraContextualButton) {
    const command = button.command
    if (command.type === 'dismiss') {
      store.dismiss(tipId)
      return
    }
    if (navigate(command)) store.dismiss(tipId)
  }

  return { run }
}
